import { Coins, RefreshCw, Save, ShieldCheck, Trophy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addLoyaltyAdjustment,
  loadLoyalty,
  saveLoyaltySettings,
  type LoyaltySettings,
  type LoyaltyViewer,
} from './loyalty-api';
import './loyalty.css';

const RULES: Array<{
  key:
    | 'followPoints'
    | 'subscriptionPoints'
    | 'resubscriptionPoints'
    | 'giftPointsPerSubscription'
    | 'cheerPointsPer100Bits'
    | 'raidPoints'
    | 'redemptionPoints';
  label: string;
  hint: string;
}> = [
  { key: 'followPoints', label: 'Follow', hint: 'per follow' },
  { key: 'subscriptionPoints', label: 'Subscribe / renew', hint: 'base award' },
  { key: 'resubscriptionPoints', label: 'Resub message bonus', hint: 'additive' },
  { key: 'giftPointsPerSubscription', label: 'Gift subs', hint: 'per gifted sub' },
  { key: 'cheerPointsPer100Bits', label: 'Bits', hint: 'per 100 bits' },
  { key: 'raidPoints', label: 'Raid', hint: 'per raid' },
  { key: 'redemptionPoints', label: 'Channel Points', hint: 'per redemption' },
];

function when(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
    : 'Unknown';
}

export function LoyaltyPage({ role }: { role: 'owner' | 'operator' }) {
  const [settings, setSettings] = useState<LoyaltySettings | null>(null);
  const [viewers, setViewers] = useState<LoyaltyViewer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [adjustment, setAdjustment] = useState({ twitchUserId: '', amount: '100', reason: '' });

  const refresh = useCallback(async () => {
    try {
      const next = await loadLoyalty();
      setSettings(next.settings);
      setViewers(next.viewers);
      setTotal(next.total);
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Loyalty data could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const active = settings?.automaticEnabled === true &&
    !(settings.pauseInSafeMode && settings.safeModeActive);
  const topViewer = viewers[0] ?? null;
  const ruleTotal = useMemo(
    () => settings ? RULES.filter(({ key }) => settings[key] > 0).length : 0,
    [settings],
  );

  async function save(next: LoyaltySettings, success: string) {
    if (role !== 'owner') return;
    setBusy(true);
    setMessage('');
    try {
      const saved = await saveLoyaltySettings(next);
      setSettings(saved);
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Loyalty settings could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleAutomatic() {
    if (!settings || role !== 'owner') return;
    if (!settings.automaticEnabled &&
      !window.confirm('Enable automatic FFSControl loyalty earning?\n\nReal Twitch EventSub events will begin creating permanent ledger credits using the currently saved rules.')) {
      return;
    }
    await save(
      { ...settings, automaticEnabled: !settings.automaticEnabled },
      settings.automaticEnabled ? 'Automatic earning disabled.' : 'Automatic earning enabled.',
    );
  }

  async function recordAdjustment() {
    if (role !== 'owner') return;
    const amount = Number(adjustment.amount);
    if (!/^\d{1,30}$/.test(adjustment.twitchUserId) ||
        !Number.isSafeInteger(amount) || amount === 0 || Math.abs(amount) > 1_000_000 ||
        !adjustment.reason.trim()) {
      setMessage('Enter a Twitch user ID, a non-zero whole-number adjustment, and an audited reason.');
      return;
    }
    if (!window.confirm(`Record a permanent loyalty adjustment of ${amount.toLocaleString('en-GB')} points?\n\nThis writes an immutable ledger entry.`)) return;
    setBusy(true);
    setMessage('');
    try {
      await addLoyaltyAdjustment({
        twitchUserId: adjustment.twitchUserId,
        amount,
        reason: adjustment.reason.trim(),
      });
      setAdjustment((current) => ({ ...current, reason: '' }));
      await refresh();
      setMessage('Loyalty adjustment recorded.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Loyalty adjustment failed.');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !settings) return <div className="ffs-next-loading">Loading Loyalty…</div>;

  return (
    <div className="ffs-workspace-page ffs-loyalty-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div>
          <small>VIEWER LOYALTY FOUNDATION</small>
          <h2>Loyalty &amp; Points</h2>
          <p>Evidence-backed points, immutable balances and rankings. The foundation is built, but automatic earning stays deliberate.</p>
        </div>
        <div className="ffs-loyalty-hero-actions">
          <span className={`ffs-status ${active ? 'good' : 'warning'}`}>
            {active ? 'EARNING ACTIVE' : 'NOT IN USE'}
          </span>
          <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void refresh()}>
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </section>

      <section className="ffs-loyalty-status-grid">
        <article className={active ? 'good' : ''}><span>Automatic earning</span><strong>{active ? 'ACTIVE' : 'OFF'}</strong><small>{settings?.safeModeActive && settings.pauseInSafeMode ? 'Paused by Safe Mode' : active ? 'Real Twitch events can award points' : 'No automatic points are being awarded'}</small></article>
        <article><span>Configured rules</span><strong>{ruleTotal.toLocaleString('en-GB')} / {RULES.length}</strong><small>Zero-value rules award nothing</small></article>
        <article><span>Known balances</span><strong>{total.toLocaleString('en-GB')}</strong><small>Canonical Twitch user IDs</small></article>
        <article><span>Current leader</span><strong>{topViewer ? topViewer.displayName || topViewer.login || topViewer.twitchUserId : '—'}</strong><small>{topViewer ? `${topViewer.balance.toLocaleString('en-GB')} points` : 'No balance data yet'}</small></article>
      </section>

      <section className="ffs-loyalty-main-grid">
        <article className="ffs-workspace-card ffs-loyalty-rules-card">
          <header>
            <div><small>OWNER CONFIGURATION</small><h3>Earning rules</h3></div>
            <ShieldCheck size={18} />
          </header>

          <div className="ffs-loyalty-master">
            <div>
              <strong>{settings?.automaticEnabled ? 'Automatic earning is enabled' : 'Automatic earning is off'}</strong>
              <span>Enabling this causes real verified Twitch events to create permanent ledger credits.</span>
            </div>
            <button
              type="button"
              className={`ffs-button ${settings?.automaticEnabled ? 'warning' : 'primary'}`}
              disabled={busy || role !== 'owner' || !settings}
              onClick={() => void toggleAutomatic()}
            >
              {settings?.automaticEnabled ? 'Turn Earning Off' : 'Enable Automatic Earning'}
            </button>
          </div>

          <label className="ffs-loyalty-safe-mode">
            <input
              type="checkbox"
              checked={settings?.pauseInSafeMode ?? true}
              disabled={busy || role !== 'owner' || !settings}
              onChange={(event) => setSettings((current) => current ? { ...current, pauseInSafeMode: event.target.checked } : current)}
            />
            <span><strong>Pause earning in Safe Mode</strong><small>Recommended. No automatic awards are created while Safe Mode is active.</small></span>
          </label>

          <div className="ffs-loyalty-rule-grid">
            {settings ? RULES.map(({ key, label, hint }) => (
              <label key={key}>
                <span>{label}<small>{hint}</small></span>
                <input
                  type="number"
                  min="0"
                  max="100000"
                  step="1"
                  value={settings[key]}
                  disabled={busy || role !== 'owner'}
                  onChange={(event) => {
                    const value = Math.max(0, Math.min(100000, Math.trunc(Number(event.target.value) || 0)));
                    setSettings({ ...settings, [key]: value });
                  }}
                />
              </label>
            )) : null}
          </div>

          <div className="ffs-loyalty-save-row">
            <div><span>Last saved</span><strong>{when(settings?.updatedAt ?? null)}</strong><small>{settings?.updatedBy ? `by ${settings.updatedBy}` : 'Defaults / not deliberately configured yet'}</small></div>
            <button type="button" className="ffs-button primary" disabled={busy || role !== 'owner' || !settings} onClick={() => settings && void save(settings, 'Loyalty rules saved.')}>
              <Save size={14} /> Save Rules
            </button>
          </div>

          <p className="ffs-workspace-note">
            Watch time, inferred presence and chat activity are deliberately excluded. Viewer-facing !points / !rank commands are not enabled yet.
          </p>

          {role === 'owner' ? (
            <details className="ffs-loyalty-adjustment">
              <summary>Manual ledger adjustment</summary>
              <p>For corrections or deliberate grants only. Entries are permanent and audited.</p>
              <div>
                <label><span>Twitch user ID</span><input value={adjustment.twitchUserId} onChange={(event) => setAdjustment({ ...adjustment, twitchUserId: event.target.value })} /></label>
                <label><span>Points</span><input type="number" value={adjustment.amount} onChange={(event) => setAdjustment({ ...adjustment, amount: event.target.value })} /></label>
                <label><span>Audited reason</span><input value={adjustment.reason} onChange={(event) => setAdjustment({ ...adjustment, reason: event.target.value })} /></label>
                <button type="button" className="ffs-button warning" disabled={busy} onClick={() => void recordAdjustment()}>Record Adjustment</button>
              </div>
            </details>
          ) : null}
        </article>

        <article className="ffs-workspace-card ffs-loyalty-leaderboard-card">
          <header><div><small>LEDGER BALANCES</small><h3>Leaderboard</h3></div><Trophy size={18} /></header>
          <div className="ffs-loyalty-leaderboard-head"><span>Rank</span><span>Viewer</span><span>Balance</span></div>
          <div className="ffs-loyalty-leaderboard">
            {viewers.map((viewer) => (
              <div key={viewer.twitchUserId}>
                <strong>#{viewer.rank.toLocaleString('en-GB')}</strong>
                <span><b>{viewer.displayName || viewer.login || viewer.twitchUserId}</b><small>{viewer.login ? `@${viewer.login}` : viewer.twitchUserId}</small></span>
                <em>{viewer.balance.toLocaleString('en-GB')}</em>
              </div>
            ))}
            {viewers.length === 0 ? <div className="ffs-empty-state"><Coins size={16} /> No loyalty balances yet.</div> : null}
          </div>
          <p className="ffs-workspace-note">Showing the first {viewers.length.toLocaleString('en-GB')} of {total.toLocaleString('en-GB')} ranked viewers.</p>
        </article>
      </section>
    </div>
  );
}
